import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import {
  createDomain,
  deleteDomain,
  getDomain,
  getDomains,
  registerStripePaymentMethodDomain,
  updateDomain,
  verifyDomain,
  type DomainOrganizationScope,
  type DomainScope,
  type DomainShowKeyEntries,
} from './domains.api'
import type { Domain, DomainIdInput, DomainInput, UpdateDomainInput } from './domains.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type DomainIndexQueryKey = [AdminQueryRoot & DomainOrganizationScope]
type DomainShowQueryKey = [AdminQueryRoot & DomainShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const domainQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'domain' }, { organizationId }),
  ],
  index: ({ organizationId }: OrganizationScope = {}): DomainIndexQueryKey =>
    buildScopedAdminQueryKey(domainQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
    }),
  show: ({ organizationId, domainId }: DomainScope): DomainShowQueryKey =>
    buildScopedAdminQueryKey(domainQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      domainId: Number(domainId),
    }),
}

export const domainQueries = {
  index: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: domainQueryKeys.index({ organizationId }),
      queryFn: getDomains,
    }),

  show: ({ organizationId, domainId }: DomainScope) =>
    queryOptions({
      queryKey: domainQueryKeys.show({ organizationId, domainId }),
      queryFn: getDomain,
      // Use data from the index route if available
      initialData: () =>
        queryClient
          .getQueryData<Domain[]>(domainQueryKeys.index({ organizationId }))
          ?.find((d) => d.id === Number(domainId)),
      initialDataUpdatedAt: () =>
        queryClient.getQueryState(domainQueryKeys.index({ organizationId }))?.dataUpdatedAt,
    }),
}

export function useDomains({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof domainQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...domainQueries.index({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useDomain(
  domainId: DomainIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof domainQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...domainQueries.show({ organizationId: resolvedOrganizationId, domainId }),
    ...params,
  })
}

export function useCreateDomainMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: DomainInput) => createDomain({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: domainQueryKeys.all({ organizationId }),
      }),
  })
}

export function useUpdateDomainMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: UpdateDomainInput) => updateDomain({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: domainQueryKeys.all({ organizationId }),
      }),
  })
}

const cleanupDeletedDomainQueries = ({
  organizationId,
  domainId,
}: {
  organizationId: number
  domainId: DomainIdInput
}) => {
  queryClient.removeQueries({
    queryKey: domainQueryKeys.show({ organizationId, domainId }),
  })

  return queryClient.invalidateQueries({ queryKey: domainQueryKeys.all({ organizationId }) })
}

export function useDeleteDomainMutation({ cleanupOnSuccess = true } = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedDomain = (domainId: DomainIdInput) =>
    cleanupDeletedDomainQueries({ organizationId, domainId })

  const mutation = useMutation({
    mutationFn: (domainId: DomainIdInput) => deleteDomain({ organizationId, domainId }),
    onSuccess: (_, domainId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedDomain(domainId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedDomain,
  }
}

export function useVerifyDomainMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (domainId: DomainIdInput) => verifyDomain({ organizationId, domainId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: domainQueryKeys.all({ organizationId }),
      }),
  })
}

export function useRegisterStripePaymentMethodDomainMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (domainId: DomainIdInput) =>
      registerStripePaymentMethodDomain({ organizationId, domainId }),
    onSuccess: (domain, domainId) => {
      queryClient.setQueryData(domainQueryKeys.show({ organizationId, domainId }), domain)

      return queryClient.invalidateQueries({
        queryKey: domainQueryKeys.all({ organizationId }),
      })
    },
  })
}
